import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class IosDetectionService {
  private readonly MIN_SUPPORTED_IOS_VERSION = 16;
  private readonly MIN_SUPPORTED_IOS_VERSION_STRING = '16.0';

  /**
   * Detects if the current device is running iOS 15 or earlier
   * @returns true if iOS version is 15 or earlier, false otherwise
   */
  isUnsupportedIosVersion(): boolean {
    const userAgent = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);

    if (!isIOS) {
      return false;
    }

    const iosVersion = this.getIosVersion(userAgent);
    if (!iosVersion) {
      return false;
    }

    return iosVersion < this.MIN_SUPPORTED_IOS_VERSION;
  }

  /**
   * Gets the iOS version from user agent string
   * @param userAgent - The user agent string
   * @returns iOS version number or null if not found
   */
  private getIosVersion(userAgent: string): number | null {
    // Match iOS version in user agent string - handles both OS X_Y and OS X_Y_Z formats
    const match = userAgent.match(/OS (\d+)_(\d+)(?:_(\d+))?/);
    if (match) {
      const majorVersion = parseInt(match[1], 10);
      const minorVersion = parseInt(match[2], 10);

      // For versions like 15.99, we need to handle them as 15.99, not 15 + 99/10
      // The minor version in user agent can be > 9, so we need to be more careful
      if (minorVersion >= 10) {
        // For minor versions >= 10, treat as decimal part
        return majorVersion + minorVersion / 100;
      } else {
        // For minor versions < 10, treat as normal decimal
        return majorVersion + minorVersion / 10;
      }
    }
    return null;
  }

  /**
   * Gets the current iOS version string
   * @returns iOS version string or null if not iOS
   */
  getCurrentIosVersion(): string | null {
    const userAgent = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);

    if (!isIOS) {
      return null;
    }

    const match = userAgent.match(/OS (\d+)_(\d+)/);
    if (match) {
      return `${match[1]}.${match[2]}`;
    }
    return null;
  }

  /**
   * Gets the minimum supported iOS version
   * @returns Minimum supported iOS version string
   */
  getMinSupportedVersion(): string {
    return this.MIN_SUPPORTED_IOS_VERSION_STRING;
  }

  /**
   * Checks if the device is an iPad
   * @returns true if device is iPad, false otherwise
   */
  isIpad(): boolean {
    return /iPad/.test(navigator.userAgent);
  }

  /**
   * Gets device information for debugging
   * @returns Object containing device information
   */
  getDeviceInfo(): { isIOS: boolean; isIpad: boolean; version: string | null; isUnsupported: boolean; deviceType: string; userAgent: string } {
    const userAgent = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);
    const isIpad = this.isIpad();

    let deviceType = 'Unknown';
    if (isIpad) {
      deviceType = 'iPad';
    } else if (/iPhone/.test(userAgent)) {
      deviceType = 'iPhone';
    } else if (/iPod/.test(userAgent)) {
      deviceType = 'iPod';
    }

    return {
      isIOS,
      isIpad,
      version: this.getCurrentIosVersion(),
      isUnsupported: this.isUnsupportedIosVersion(),
      deviceType,
      userAgent,
    };
  }
}
